import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDocumentUpload } from 'Core/hooks';
import CustomPropTypes from 'CustomPropTypes';
import { downloadImage } from 'Core/utils';
import Alert from 'react-bootstrap/Alert';
import { isNotNull } from 'Core/utils';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { FooterButton } from 'Core/components';
import { Dropdown, DropdownButton, ButtonGroup } from 'react-bootstrap';
import {
  printlabel,
  printlabelfornotinproduction,
  scanlabel,
  reprintlabel,
  lostlabelreprint,
  printcustomlabels,
} from '../production/store/slice';
import { PRODUCTION_PRINT_ACTIONS } from '../../core/constants/constants';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { Activity, Codesandbox, Printer } from 'feather-icons-react/build/IconComponents';
import { Edit } from 'react-feather';

const PART_PRINT_DEFAULT_WORK_TICKET = '1002';
const PART_PRINT_DEFAULT_PROCESS_ID = '1';
/** Set to `true` to allow editing work ticket / process ID via the pencil icon. */
const PART_PRINT_HEADER_EDIT_ENABLED = false;

const initData = {
  id: 0,
  ProductionResultsId: 0,
  fileName: '',
  filePath: '',
  relativePath: '',
};

/** Build JSON body for APIs that deserialize to C# `PrintLabel` (camelCase JSON, int fields as numbers, `numberOfLabels` matches lowercase property name). */
function buildPrintLabelApiBody({
  partId,
  processId,
  userId,
  workTicketNumber,
  status,
  quantity,
  numberOfLabels,
  printerId,
  materialBatchNumber,
  poNumber,
  supplierName,
  provider,
  batchNumber,
  lotNumber,
}) {
  return {
    partId: Math.trunc(Number(partId)) || 0,
    processId: Math.trunc(Number(processId)) || 0,
    userId: String(userId ?? ''),
    workTicketNumber: String(workTicketNumber ?? ''),
    status: String(status ?? ''),
    quantity: Math.max(1, Math.trunc(Number(quantity)) || 1),
    numberOfLabels: Math.max(1, Math.trunc(Number(numberOfLabels)) || 1),
    printerId: printerId != null && printerId !== '' ? String(printerId) : '',
    materialBatchNumber: materialBatchNumber == null ? null : String(materialBatchNumber),
    poNumber: String(poNumber ?? ''),
    poNo: String(poNumber ?? ''),
    supplierName: String(supplierName ?? ''),
    supName: String(supplierName ?? ''),
    provider: String(provider ?? ''),
    batchNumber: String(batchNumber ?? ''),
    lotNumber: String(lotNumber ?? ''),
    lotNo: String(lotNumber ?? ''),
    isPartModule: true,
    moduleName: 'PartModule',
  };
}

function resolvePartsInBoxQuantity(partsinaBox) {
  if (partsinaBox === null || partsinaBox === undefined || partsinaBox === '') return 1;
  const x = Number(partsinaBox);
  if (!Number.isFinite(x)) return 1;
  const t = Math.trunc(x);
  return t > 0 ? t : 1;
}

function formatPartModuleBatchDate(date = new Date()) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}${month}${year}`;
}

function isGeneratedInventorySerial(value) {
  return /^A\d+D\d+I\d+$/i.test(String(value ?? '').trim());
}

const PartmastersPrint = ({ data, printers, onAction, onCommentUpdate, key }) => {
  const dispatch = useDispatch();
  const [workTicketInput, setWorkTicketInput] = useState(PART_PRINT_DEFAULT_WORK_TICKET);
  const [batchNumber, setBatchNumber] = useState('');
  const [action, setAction] = useState('');
  const [printer, setPrinter] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [customQuantity, setCustomQuantity] = useState('');
  const [numberOfLabels, setNumberOfLabels] = useState('');
  const [processIdInput, setProcessIdInput] = useState(PART_PRINT_DEFAULT_PROCESS_ID);
  const [poNumber, setPoNumber] = useState('');
  const [isSupplierProvided, setIsSupplierProvided] = useState(false);
  const [supplierName, setSupplierName] = useState('');
  const [editingWorkTicket, setEditingWorkTicket] = useState(false);
  const [editingProcessId, setEditingProcessId] = useState(false);
  const [error, setError] = useState({
    isError: false,
    fields: '',
  });

  const [toolFilesInputs, setToolFileInputs] = useState(initData);
  const [toolFiles, setToolFiles] = useState(data);
  const updateToolFileField = (field, value) => {
    setToolFileInputs(prevToolFileInputs => ({
      ...prevToolFileInputs,
      [field]: value,
    }));
  };

  const { uploadDocuments, loading } = useDocumentUpload();
  const [isHovering, setIsHovering] = useState(false);

  const partBoxQty = useMemo(
    () => resolvePartsInBoxQuantity(data?.partsinaBox),
    [data?.partsinaBox, data?.partId],
  );
  const normalizedPartClass = String(data?.partClass ?? data?.class ?? data?.Class ?? '')
    .trim()
    .toUpperCase();
  const showPurchaseFields = normalizedPartClass !== '' && normalizedPartClass !== 'FG';

  useEffect(() => {
    setCustomQuantity(String(partBoxQty));
  }, [data?.partId, data?.partsinaBox, partBoxQty]);

  useEffect(() => {
    setWorkTicketInput(PART_PRINT_DEFAULT_WORK_TICKET);
    setBatchNumber('');
    setProcessIdInput(PART_PRINT_DEFAULT_PROCESS_ID);
    setPoNumber('');
    setIsSupplierProvided(false);
    setSupplierName('');
    setEditingWorkTicket(false);
    setEditingProcessId(false);
  }, [data?.partId]);

  const handleFileUpload = async event => {
    const files = Array.from(event.target.files);

    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('UploadCategory', 'Production-ProductionPrint');

    const result = await uploadDocuments(formData);
    if (result) {
      const newFile = {
        id: Math.floor(Math.random() * 101),
        fileName: result.fileName,
        filePath: result.filePath,
        relativePath: result.relativePath,
      };
      const newFiles = [...toolFiles, newFile];

      setToolFiles(newFiles);
      setToolFileInputs(initData);
    }
  };

  const downloadImageFn = (e, img) => {
    e.stopPropagation();
    downloadImage(img);
  };

  const removeFile = fileId => {
    const newFiles = toolFiles.filter(toolFile => toolFile.id !== fileId);
    setToolFiles(newFiles);
  };

  const onBtnAction = e => {
    const action = e.target.id;
    setAction(action);
    setNumberOfLabels('');
  };

  const isActionDisabled = actionVal => {
    return action && action != actionVal;
  };

  const selectedPrinterName = () => {
    const selectedPrinter = printers.filter(print => print.printerId == printer);
    return selectedPrinter[0] ? selectedPrinter[0].printerName : '';
  };

  const showPrinters = () => {
    return (
      <>
        <Form.Label htmlFor="printers">Printer</Form.Label>
        <DropdownButton
          className="dropdown-print"
          style={{ width: '100%' }}
          onSelect={setPrinter}
          id="printers"
          title={action ? selectedPrinterName() : 'Select Printer'}
        >
          {printers.map(printer => (
            <Dropdown.Item eventKey={printer.printerId} key={printer.printerId}>
              {printer.printerName}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </>
    );
  };

  const resetActions = () => {
    setAction('');
    setPrinter('');
    setCustomQuantity(String(partBoxQty));
    setSerialNumber('');
    setBatchNumber('');
    setPoNumber('');
    setSupplierName('');
    setIsSupplierProvided(false);
    setNumberOfLabels('');
    setError({
      isError: false,
      fields: '',
    });
  };

  const checkError = (body, fields) => {
    const errorFields = [];
    let printerField = false;
    Object.keys(body).forEach(key => {
      if (fields.includes(key) && !body[key]) {
        if(key != 'printerId')
          errorFields.push(key);
        else
          printerField = true;
      }
    });
    if (errorFields.length || printerField)
      setError({
        isError: true,
        fields: `${printerField ? 'Please select a Printer. ' : ''}
                  ${errorFields.join(', ')} ${errorFields.length > 1 ? 'are' : 'is'} mandatory`,
      });
    return errorFields.length > 0 || printerField;
  };

  const onActionSubmit = () => {
    const workTicketNumber =
      (workTicketInput ?? '').trim() || PART_PRINT_DEFAULT_WORK_TICKET;
    const processIdFromHeader = (() => {
      const raw = (processIdInput ?? '').trim();
      if (raw !== '') {
        const n = Number(raw);
        return Number.isFinite(n) ? Math.trunc(n) : 0;
      }
      return Number(PART_PRINT_DEFAULT_PROCESS_ID) || 1;
    })();
    const statusForApi = String(
      data?.status ?? data?.Status ?? data?.processStatus ?? data?.ProcessStatus ?? '',
    ).trim();
    const batchNumberTrim = String(batchNumber ?? '').trim();
    const poNumberTrim = String(poNumber ?? '').trim();
    const supplierNameTrim = String(supplierName ?? '').trim();
    const providerForApi = showPurchaseFields
      ? isSupplierProvided
        ? 'Supplier'
        : 'Purchase'
      : '';
    const printBatchNumber = showPurchaseFields
      ? `${formatPartModuleBatchDate()} ${poNumberTrim}`.trim()
      : `${processIdFromHeader} ${workTicketNumber}`.trim();
    const materialBatchNumberForApi = showPurchaseFields ? null : batchNumberTrim;
    const lotNumberForApi = showPurchaseFields ? batchNumberTrim : '';
    /** Prefer part row qty, else parts-in-box (see resolvePartsInBoxQuantity). */
    const printQuantity = (() => {
      const q = Number(data?.quantity);
      if (Number.isFinite(q) && q > 0) return Math.trunc(q);
      return partBoxQty;
    })();
    /** SP_PrintLabel @Quantity — int >= 1 when the PROCESS join leaves @V_Quantity unset. */
    const quantityForSp = Math.max(1, Math.trunc(Number(printQuantity)) || 1);

    switch (action) {
      case PRODUCTION_PRINT_ACTIONS.PRINT_LABEL: {
        if (processIdFromHeader <= 0) {
          dispatch(
            showToast({
              message: 'Enter the Production Process ID (from the Production screen) so the print can match your job.',
              type: 'danger',
            }),
          );
          return;
        }
        const labelCountRaw = Number(numberOfLabels);
        const numberOfLabelsInt =
          Number.isFinite(labelCountRaw) && labelCountRaw > 0 ? Math.trunc(labelCountRaw) : 1;
        const body = buildPrintLabelApiBody({
          partId: data.partId ?? 0,
          processId: processIdFromHeader,
          userId: localStorage.getItem('UserId') || '',
          workTicketNumber,
          status: statusForApi,
          quantity: quantityForSp,
          numberOfLabels: numberOfLabelsInt,
          printerId: printer,
          materialBatchNumber: materialBatchNumberForApi,
          poNumber: poNumberTrim,
          supplierName: supplierNameTrim,
          provider: providerForApi,
          batchNumber: printBatchNumber,
          lotNumber: lotNumberForApi,
        });
        if (!checkError(body, ['printerId'])) {
          dispatch(printlabel(body));
          resetActions();
        }
        break;
      }
      case PRODUCTION_PRINT_ACTIONS.PRINT_NOTIN_PRODUCTION: {
        const labelCountRaw = Number(numberOfLabels);
        const numberOfLabelsInt =
          Number.isFinite(labelCountRaw) && labelCountRaw > 0 ? Math.trunc(labelCountRaw) : 1;
        const body = buildPrintLabelApiBody({
          partId: data.partId,
          processId: processIdFromHeader,
          userId: localStorage.getItem('UserId'),
          workTicketNumber,
          status: statusForApi,
          quantity: quantityForSp,
          numberOfLabels: numberOfLabelsInt,
          printerId: printer,
          materialBatchNumber: materialBatchNumberForApi,
          poNumber: poNumberTrim,
          supplierName: supplierNameTrim,
          provider: providerForApi,
          batchNumber: printBatchNumber,
          lotNumber: lotNumberForApi,
        });
        if (!checkError(body, ['printerId'])) {
          dispatch(printlabelfornotinproduction(body));
          resetActions();
        }
        break;
      }
      case PRODUCTION_PRINT_ACTIONS.SCAN_LABEL: {
        const body = {
          partId: data.partId,
          serialNumber: serialNumber,
          processId: processIdFromHeader,
          scannedUserId: localStorage.getItem('UserId'),
          printerId: printer,
          batchNumber: printBatchNumber,
          materialBatchNumber: materialBatchNumberForApi,
          lotNumber: lotNumberForApi,
          lotNo: lotNumberForApi,
          poNumber: poNumberTrim,
          poNo: poNumberTrim,
          supplierName: supplierNameTrim,
          supName: supplierNameTrim,
          provider: providerForApi,
          isPartModule: true,
          moduleName: 'PartModule',
          quantity: quantityForSp,
        };

        if (!checkError(body, ['printerId', 'serialNumber'])) {
          dispatch(scanlabel(body));
          resetActions();
        }
        break;
      }
      case PRODUCTION_PRINT_ACTIONS.REPRINT_LABEL: {
        const body = {
          partId: data.partId,
          serialNumber: serialNumber,
          processId: processIdFromHeader,
          scannedUserId: localStorage.getItem('UserId'),
          printerId: printer,
          batchNumber: printBatchNumber,
          materialBatchNumber: materialBatchNumberForApi,
          lotNumber: lotNumberForApi,
          lotNo: lotNumberForApi,
          poNumber: poNumberTrim,
          poNo: poNumberTrim,
          supplierName: supplierNameTrim,
          supName: supplierNameTrim,
          provider: providerForApi,
          isPartModule: true,
          moduleName: 'PartModule',
          quantity: quantityForSp,
        };
        if (!checkError(body, ['printerId', 'serialNumber'])) {
          dispatch(reprintlabel(body));
          resetActions();
        }
        break;
      }
      case PRODUCTION_PRINT_ACTIONS.LOST_LABEL_REPRINT: {
        if (!showPurchaseFields && isGeneratedInventorySerial(batchNumberTrim)) {
          dispatch(
            showToast({
              message: 'Enter the material batch number in Material Batch Number. Serial number cannot be used as material batch number.',
              type: 'danger',
            }),
          );
          return;
        }
        const labelCountRaw = Number(numberOfLabels);
        const numberOfLabelsInt =
          Number.isFinite(labelCountRaw) && labelCountRaw > 0 ? Math.trunc(labelCountRaw) : 1;
        const body = buildPrintLabelApiBody({
          partId: data.partId,
          processId: processIdFromHeader,
          userId: localStorage.getItem('UserId'),
          workTicketNumber,
          status: statusForApi,
          quantity: quantityForSp,
          numberOfLabels: numberOfLabelsInt,
          printerId: printer,
          materialBatchNumber: materialBatchNumberForApi,
          poNumber: poNumberTrim,
          supplierName: supplierNameTrim,
          provider: providerForApi,
          batchNumber: printBatchNumber,
          lotNumber: lotNumberForApi,
        });
        if (!checkError(body, ['printerId'])) {
          dispatch(lostlabelreprint(body));
          resetActions();
        }
        break;
      }
      case PRODUCTION_PRINT_ACTIONS.PRINT_CUSTOM_QUANTITY_LABEL: {
        const customQtyForSp = Math.max(1, Math.trunc(Number(customQuantity)) || quantityForSp);
        const numberOfLabelsCustom = Math.max(1, Math.trunc(Number(numberOfLabels)) || 1);
        const body = buildPrintLabelApiBody({
          partId: data.partId,
          processId: processIdFromHeader,
          userId: localStorage.getItem('UserId'),
          workTicketNumber,
          status: statusForApi,
          quantity: customQtyForSp,
          numberOfLabels: numberOfLabelsCustom,
          printerId: printer,
          materialBatchNumber: materialBatchNumberForApi,
          poNumber: poNumberTrim,
          supplierName: supplierNameTrim,
          provider: providerForApi,
          batchNumber: printBatchNumber,
          lotNumber: lotNumberForApi,
        });
        if (!checkError(body, ['printerId', 'quantity'])) {
          dispatch(printcustomlabels(body));
          resetActions();
        }
        break;
      }
    }
  };

  const showNoOfLabelsFor = [
    PRODUCTION_PRINT_ACTIONS.LOST_LABEL_REPRINT,
    PRODUCTION_PRINT_ACTIONS.PRINT_CUSTOM_QUANTITY_LABEL,
    PRODUCTION_PRINT_ACTIONS.PRINT_NOTIN_PRODUCTION,
  ];

  const showSecondaryQuantityWithLabelsFor = [
    PRODUCTION_PRINT_ACTIONS.LOST_LABEL_REPRINT,
    PRODUCTION_PRINT_ACTIONS.PRINT_NOTIN_PRODUCTION,
  ];

  const lockedPartsInBoxQuantityField = (
    <div className="d-flex flex-column mt-3">
      <Form.Label>Quantity</Form.Label>
      <Form.Control
        type="text"
        readOnly
        className="bg-light"
        value={String(partBoxQty)}
        tabIndex={-1}
      />
    </div>
  );

  return (
    <>
      <div className="w-100 row justify-content-between g-3 g-lg-4">
        <div className="mb-4 col-12 col-lg-6">
          <div className="d-flex flex-column">
            <label className="mb-1">Part Number</label>
            <h5>{isNotNull(data, 'partNumber')}</h5>
          </div>
        </div>
        <div className="mb-4 col-12 col-lg-6">
          <div className="d-flex flex-column">
            {showPurchaseFields ? (
              <>
                <Form.Label>Purchase/Supplier Provided</Form.Label>
                <ButtonGroup className="flex-wrap" aria-label="Purchase or supplier provided">
                  <Button
                    type="button"
                    variant={!isSupplierProvided ? 'primary' : 'outline-primary'}
                    onClick={() => {
                      setIsSupplierProvided(false);
                      setSupplierName('');
                    }}
                  >
                    Purchase
                  </Button>
                  <Button
                    type="button"
                    variant={isSupplierProvided ? 'primary' : 'outline-primary'}
                    onClick={() => setIsSupplierProvided(true)}
                  >
                    Supplier Provided
                  </Button>
                </ButtonGroup>
                {isSupplierProvided && (
                  <div className="d-flex flex-column mt-3">
                    <Form.Label htmlFor="partmasters-supplier-name">Supplier Name</Form.Label>
                    <Form.Control
                      type="text"
                      id="partmasters-supplier-name"
                      placeholder="Enter supplier name"
                      value={supplierName}
                      onChange={e => setSupplierName(e.target.value)}
                      autoComplete="off"
                      style={{ maxWidth: '280px' }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <Form.Label htmlFor={editingWorkTicket ? 'partmasters-work-ticket' : undefined}>Work Ticket</Form.Label>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  {editingWorkTicket ? (
                    <Form.Control
                      type="text"
                      id="partmasters-work-ticket"
                      className="flex-grow-1"
                      style={{ maxWidth: '280px' }}
                      value={workTicketInput}
                      onChange={e => setWorkTicketInput(e.target.value)}
                    />
                  ) : (
                    <h5 className="mb-0">
                      {(workTicketInput ?? '').trim() || PART_PRINT_DEFAULT_WORK_TICKET}
                    </h5>
                  )}
                  <button
                    type="button"
                    className="btn btn-link p-0 d-inline-flex align-items-center text-secondary"
                    disabled={!PART_PRINT_HEADER_EDIT_ENABLED}
                    aria-label={editingWorkTicket ? 'Done editing work ticket' : 'Edit work ticket'}
                    onClick={() => PART_PRINT_HEADER_EDIT_ENABLED && setEditingWorkTicket(v => !v)}
                  >
                    <Edit size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="w-100 row justify-content-between g-3 g-lg-4">
        <div className="mb-4 col-12 col-lg-6">
          <div className="d-flex flex-column">
            {showPurchaseFields ? (
              <>
                <Form.Label htmlFor="partmasters-po-number">PO</Form.Label>
                <Form.Control
                  type="text"
                  id="partmasters-po-number"
                  className="flex-grow-1"
                  style={{ maxWidth: '280px' }}
                  placeholder="Enter PO"
                  value={poNumber}
                  onChange={e => setPoNumber(e.target.value)}
                  autoComplete="off"
                />
              </>
            ) : (
              <>
                <Form.Label htmlFor={editingProcessId ? 'partmasters-process-id' : undefined}>Process ID</Form.Label>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  {editingProcessId ? (
                    <Form.Control
                      type="text"
                      id="partmasters-process-id"
                      className="flex-grow-1"
                      style={{ maxWidth: '280px' }}
                      value={processIdInput}
                      onChange={e => setProcessIdInput(e.target.value)}
                    />
                  ) : (
                    <h5 className="mb-0">
                      {(processIdInput ?? '').trim() || PART_PRINT_DEFAULT_PROCESS_ID}
                    </h5>
                  )}
                  <button
                    type="button"
                    className="btn btn-link p-0 d-inline-flex align-items-center text-secondary"
                    disabled={!PART_PRINT_HEADER_EDIT_ENABLED}
                    aria-label={editingProcessId ? 'Done editing process ID' : 'Edit process ID'}
                    onClick={() => PART_PRINT_HEADER_EDIT_ENABLED && setEditingProcessId(v => !v)}
                  >
                    <Edit size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="mb-4 col-12 col-lg-6">
          <div className="d-flex flex-column">
            <Form.Label htmlFor="partmasters-batch-number">
              {showPurchaseFields ? 'Lot Number' : 'Material Batch Number'}
            </Form.Label>
            <Form.Control
              type="text"
              id="partmasters-batch-number"
              placeholder={showPurchaseFields ? 'Enter lot number' : 'Enter material batch number'}
              value={batchNumber}
              onChange={e => setBatchNumber(e.target.value)}
              autoComplete="off"
              className="flex-grow-1"
              style={{ maxWidth: '280px' }}
            />
          </div>
        </div>
      </div>
      <div className="product-action-container w-100 pt-4 d-flex row">
        <div className="print-btns mb-4 px-lg-5 col-12 col-lg-4 d-flex flex-column h-100 justify-content-between">
          <Button
            disabled={isActionDisabled(PRODUCTION_PRINT_ACTIONS.PRINT_LABEL)}
            variant="warning"
            id="print-label"
            onClick={onBtnAction}
          >
            <Printer size={24} /> Print Label
          </Button>
          <Button
            disabled={isActionDisabled(PRODUCTION_PRINT_ACTIONS.SCAN_LABEL)}
            variant="secondary"
            id="scan-label"
            onClick={onBtnAction}
          >
            <Codesandbox size={24} /> Scan Label
          </Button>
          <Button
            disabled={isActionDisabled(PRODUCTION_PRINT_ACTIONS.REPRINT_LABEL)}
            variant="success"
            id="reprint-label"
            onClick={onBtnAction}
          >
            <Printer size={24} /> Reprint Label
          </Button>
          <Button
            disabled={isActionDisabled(PRODUCTION_PRINT_ACTIONS.LOST_LABEL_REPRINT)}
            variant="warning"
            id="lost-label-reprint"
            onClick={onBtnAction}
          >
            <Printer size={24} /> Lost Label Reprint
          </Button>
          {data.status != 'In Production' && (
            <Button
              disabled={isActionDisabled(PRODUCTION_PRINT_ACTIONS.PRINT_NOTIN_PRODUCTION)}
              variant="danger"
              id="print-not-in-production"
              onClick={onBtnAction}
            >
              <Printer size={24} /> Print Label for Parts not in production
            </Button>
          )}
          <Button
            disabled={isActionDisabled(PRODUCTION_PRINT_ACTIONS.PRINT_CUSTOM_QUANTITY_LABEL)}
            variant="success"
            id="print-custom-quantity-label"
            onClick={onBtnAction}
          >
            <Printer size={24} /> Print Custom Quantity Label
          </Button>
        </div>
        <div className="mb-4 col-12 col-lg-5 px-lg-5">
          {action ? (
            (function () {
              switch (action) {
                case PRODUCTION_PRINT_ACTIONS.PRINT_LABEL:
                  return (
                    <>
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column mt-3">{showPrinters()}</div>
                        <div className="d-flex flex-column mt-3">
                          <Form.Label htmlFor="partmasters-number-of-labels">No. of Labels</Form.Label>
                          <Form.Control
                            type="text"
                            id="partmasters-number-of-labels"
                            placeholder="No. of Labels"
                            value={numberOfLabels}
                            onChange={e => setNumberOfLabels(e.target.value)}
                          />
                        </div>
                        <div className="d-flex flex-column mt-3">
                          <Form.Label htmlFor="partmasters-print-quantity">Quantity</Form.Label>
                          <Form.Control
                            type="text"
                            id="partmasters-print-quantity"
                            readOnly
                            className="bg-light"
                            value={String(partBoxQty)}
                            tabIndex={-1}
                          />
                        </div>
                      </div>
                      <div className="mt-5 d-flex flex-column">
                        <FooterButton
                          cancelText="Cancel"
                          saveBtnClass="ms-3"
                          saveText="Submit"
                          onSave={() => onActionSubmit()}
                          onCancel={() => resetActions()}
                        />
                      </div>
                    </>
                  );
                case PRODUCTION_PRINT_ACTIONS.PRINT_CUSTOM_QUANTITY_LABEL:
                  return (
                    <>
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column mt-3">
                          <Form.Label htmlFor="customQuantity">Custom Quantity</Form.Label>
                          <Form.Control
                            type="text"
                            id="customQuantity"
                            placeholder="Enter Custom Quantity"
                            value={customQuantity}
                            onChange={e => setCustomQuantity(e.target.value)}
                          />
                        </div>
                        <div className="d-flex flex-column mt-3">
                          <Form.Label htmlFor="numberOfLabels">No. of Labels</Form.Label>
                          <Form.Control
                            type="text"
                            id="numberOfLabels"
                            placeholder="No. of Labels"
                            value={numberOfLabels}
                            onChange={e => setNumberOfLabels(e.target.value)}
                          />
                        </div>
                        <div className="d-flex flex-column mt-3">{showPrinters()}</div>
                      </div>
                      <div className="mt-5 d-flex flex-column">
                        <FooterButton
                          cancelText="Cancel"
                          saveBtnClass="ms-3"
                          saveText="Submit"
                          onSave={() => onActionSubmit()}
                          onCancel={() => resetActions()}
                        />
                      </div>
                    </>
                  );
                case PRODUCTION_PRINT_ACTIONS.SCAN_LABEL:
                  return (
                    <>
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column mt-3">
                          <Form.Label htmlFor="serialNumber">Serial Number</Form.Label>
                          <Form.Control
                            type="text"
                            id="serialNumber"
                            placeholder="Enter Serial Number"
                            value={serialNumber}
                            onChange={e => setSerialNumber(e.target.value)}
                          />
                        </div>
                        {lockedPartsInBoxQuantityField}
                        <div className="d-flex flex-column mt-3">{showPrinters()}</div>
                      </div>
                      <div className="mt-5 d-flex flex-column">
                        <FooterButton
                          cancelText="Cancel"
                          saveBtnClass="ms-3"
                          saveText="Submit"
                          onSave={() => onActionSubmit()}
                          onCancel={() => resetActions()}
                        />
                      </div>
                    </>
                  );
                case PRODUCTION_PRINT_ACTIONS.REPRINT_LABEL:
                  return (
                    <>
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column mt-3">
                          <Form.Label htmlFor="serialNumber">Serial Number</Form.Label>
                          <Form.Control
                            type="text"
                            id="serialNumber"
                            placeholder="Enter Serial Number"
                            value={serialNumber}
                            onChange={e => setSerialNumber(e.target.value)}
                          />
                        </div>
                        {lockedPartsInBoxQuantityField}
                        <div className="d-flex flex-column mt-3">{showPrinters()}</div>
                      </div>
                      <div className="mt-5 d-flex flex-column">
                        <FooterButton
                          cancelText="Cancel"
                          saveBtnClass="ms-3"
                          saveText="Submit"
                          onSave={() => onActionSubmit()}
                          onCancel={() => resetActions()}
                        />
                      </div>
                    </>
                  );
                default:
                  return action ? (
                    <>
                      <div className="d-flex flex-column mt-3">{showPrinters()}</div>
                      {
                        showNoOfLabelsFor.includes(action) &&
                        <div className="d-flex flex-column mt-3">
                          <Form.Label htmlFor="numberOfLabels">No. of Labels</Form.Label>
                          <Form.Control
                            type="text"
                            id="numberOfLabels"
                            placeholder="No. of Labels"
                            value={numberOfLabels}
                            onChange={e => setNumberOfLabels(e.target.value)}
                          />
                        </div>
                      }
                      {showSecondaryQuantityWithLabelsFor.includes(action) && lockedPartsInBoxQuantityField}
                      <div className="mt-5 d-flex flex-column">
                        <FooterButton
                          cancelText="Cancel"
                          saveBtnClass="ms-3"
                          saveText="Submit"
                          onSave={() => onActionSubmit()}
                          onCancel={() => resetActions()}
                        />
                      </div>
                    </>
                  ) : null;
              }
            })()
          ) : (
            <div className="no-data-container h-100">
              <Alert
                className="h-50px mb-0 w-100 p-3 d-flex justify-content-center align-items-center"
                key={'danger'}
                variant={'danger'}
              >
                <span className="me-3">
                  <Activity size={24} />
                </span>{' '}
                <label>Select any action from the left panel</label>
              </Alert>
            </div>
          )}
          {error && error.isError && <label className="mt-3 text-danger">{error.fields}</label>}
        </div>
      </div>
    </>
  );
};

PartmastersPrint.defaultProps = {
  onAction: () => {},
  data: [],
};

PartmastersPrint.propTypes = {
  onAction: PropTypes.func,
  data: PropTypes.shape(CustomPropTypes.ProductionResults),
};

export default PartmastersPrint;
